"use client";

import type { ComponentProps } from "react";
import Link from "next/link";
import Image from "next/image";
import { format } from "date-fns";
import { de } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { EventTag, type EventTagVariant } from "@/components/layout/EventTag";
import { MaskShape, type MaskShapeVariant } from "@/components/illustrations/MaskShape";
import { ArrowRightThin } from "@/components/icons";
import { cn } from "@/lib/utils";
import { formatEventDateRange } from "@/lib/utils/date";
import type { EventIconName } from "@/components/icons";
import { MemberLockIcon, LockedIcon } from "@/components/icons/MemberLockIcon";
import { CheckCircleFilled } from "@/components/icons/CheckCircleFilled";
import { WarningTriangle } from "@/components/icons/WarningTriangle";

type EventCardVariant = "default" | "densed";

// Map event types to their corresponding colors (matching EventTag bgClass)
const eventTypeColorClasses: Record<EventIconName, string> = {
  diaTalk: "text-sand-100",
  topicTalk: "text-sky-75",
  sprechstunde: "text-sky-100",
  community: "text-sun-100",
  reisen: "text-sun-50",
  reiseInfo: "text-sun-50",
};

const getEventTypeColorClass = (eventType: string): string => {
  if (eventType in eventTypeColorClasses) {
    return eventTypeColorClasses[eventType as EventIconName];
  }
  return "text-sky-100"; // Fallback
};

type EventCardProps = {
  variant?: EventCardVariant;
  eventType: EventTagVariant;
  date: Date;
  endDate?: Date;
  title: string;
  image?: { src: string; alt: string };
  maskShape?: MaskShapeVariant;
  ctaLabel?: string;
  ctaLink?: string;
  memberOnly?: boolean;
  isMember?: boolean;
  signedUp?: boolean;
  dateChanged?: boolean;
  isCancelled?: boolean;
  action?: React.ReactNode;
  onPreviewClick?: () => void;
  /** Oeffnet das Anmelde-Modal. Ohne diese Prop fuehrt der primaere Knopf
   *  zur Event-Seite — so verhaelt sich die Karte ueberall dort weiter wie
   *  bisher, wo das Modal noch nicht angebunden ist. */
  onSignupClick?: () => void;
} & ComponentProps<"article">;

const MaskedImage = ({
  image,
  shape = "four_vertical_even",
  eventType,
}: {
  image?: { src: string; alt: string };
  shape?: MaskShapeVariant;
  eventType?: string;
}) => {
  // If no image, show the mask shape as a colored fallback
  if (!image?.src) {
    const colorClass = eventType ? getEventTypeColorClass(eventType) : "text-sun-100";
    return (
      <div className="relative w-full h-[167px] overflow-hidden">
        <MaskShape variant={shape} className={cn("w-full h-full", colorClass)} preserveAspectRatio="none" />
      </div>
    );
  }

  return (
    <div className="relative w-full h-[167px]">
      {/* SVG mask definition */}
      <svg className="absolute" width="0" height="0">
        <defs>
          <clipPath id={`card-mask-${shape}`} clipPathUnits="objectBoundingBox">
            <MaskShapeClipPath variant={shape} />
          </clipPath>
        </defs>
      </svg>

      {/* Image with mask applied */}
      <div
        className="relative w-full h-full"
        style={{
          clipPath: `url(#card-mask-${shape})`,
        }}
      >
        <Image
          src={image.src}
          alt={image.alt}
          fill
          sizes="278px"
          className="object-cover"
        />
      </div>
    </div>
  );
};

// Generate normalized clip path based on MaskShape variant
const MaskShapeClipPath = ({ variant }: { variant: MaskShapeVariant }) => {
  switch (variant) {
    case "four_vertical_even":
      return (
        <>
          <path d="M0.363 0.5C0.363 0.776 0.282 1 0.182 1C0.081 1 0 0.776 0 0.5C0 0.224 0.081 0 0.182 0C0.282 0 0.363 0.224 0.363 0.5Z" />
          <path d="M0.576 0.5C0.576 0.776 0.494 1 0.394 1C0.294 1 0.212 0.776 0.212 0.5C0.212 0.224 0.294 0 0.394 0C0.494 0 0.576 0.224 0.576 0.5Z" />
          <path d="M0.788 0.5C0.788 0.776 0.706 1 0.606 1C0.506 1 0.424 0.776 0.424 0.5C0.424 0.224 0.506 0 0.606 0C0.706 0 0.788 0.224 0.788 0.5Z" />
          <path d="M1 0.5C1 0.776 0.919 1 0.818 1C0.718 1 0.637 0.776 0.637 0.5C0.637 0.224 0.718 0 0.818 0C0.919 0 1 0.224 1 0.5Z" />
        </>
      );
    case "two_vertical_uneven":
      return (
        <>
          <path d="M0.4 0.5C0.4 0.776 0.31 1 0.2 1C0.09 1 0 0.776 0 0.5C0 0.224 0.09 0 0.2 0C0.31 0 0.4 0.224 0.4 0.5Z" />
          <path d="M1 0.5C1 0.776 0.838 1 0.638 1C0.437 1 0.275 0.776 0.275 0.5C0.275 0.224 0.437 0 0.638 0C0.838 0 1 0.224 1 0.5Z" />
        </>
      );
    case "wave":
      return (
        <>
          <path d="M0.419 0.5C0.419 0.224 0.502 0 0.605 0C0.707 0 0.791 0.224 0.791 0.5H0.419Z" />
          <path d="M0 0.5C0 0.224 0.083 0 0.186 0C0.289 0 0.372 0.224 0.372 0.5H0Z" />
          <path d="M1 0.5C1 0.776 0.917 1 0.814 1C0.711 1 0.628 0.776 0.628 0.5H1Z" />
          <path d="M0.581 0.5C0.581 0.776 0.498 1 0.395 1C0.293 1 0.209 0.776 0.209 0.5H0.581Z" />
        </>
      );
    case "split_half_circle":
      return (
        <>
          <path d="M0 1C0.277 1 0.501 0.776 0.501 0.5C0.501 0.224 0.277 0 0 0V1Z" />
          <path d="M0.501 1C0.777 1 1 0.776 1 0.5C1 0.224 0.777 0 0.501 0V1Z" />
        </>
      );
    case "three_horizontal_even":
      return (
        <>
          <path d="M1 0.225C1 0.349 0.776 0.45 0.5 0.45C0.224 0.45 0 0.349 0 0.225C0 0.101 0.224 0 0.5 0C0.776 0 1 0.101 1 0.225Z" />
          <path d="M1 0.5C1 0.624 0.776 0.725 0.5 0.725C0.224 0.725 0 0.624 0 0.5C0 0.376 0.224 0.275 0.5 0.275C0.776 0.275 1 0.376 1 0.5Z" />
          <path d="M1 0.775C1 0.899 0.776 1 0.5 1C0.224 1 0 0.899 0 0.775C0 0.651 0.224 0.55 0.5 0.55C0.776 0.55 1 0.651 1 0.775Z" />
        </>
      );
    case "three_vertical_uneven":
      return (
        <>
          <path d="M0.4 0.5C0.4 0.776 0.31 1 0.2 1C0.09 1 0 0.776 0 0.5C0 0.224 0.09 0 0.2 0C0.31 0 0.4 0.224 0.4 0.5Z" />
          <path d="M0.6 0.5C0.6 0.776 0.51 1 0.4 1C0.29 1 0.2 0.776 0.2 0.5C0.2 0.224 0.29 0 0.4 0C0.51 0 0.6 0.224 0.6 0.5Z" />
          <path d="M1 0.5C1 0.776 0.869 1 0.708 1C0.546 1 0.415 0.776 0.415 0.5C0.415 0.224 0.546 0 0.708 0C0.869 0 1 0.224 1 0.5Z" />
        </>
      );
    case "three_vertical_uneven_mirrored":
      return (
        <>
          <path d="M1 0.5C1 0.77625 0.9105 1 0.8 1C0.6895 1 0.6 0.77625 0.6 0.5C0.6 0.22375 0.6895 0 0.8 0C0.9105 0 1 0.22375 1 0.5Z" />
          <path d="M0.785 0.5C0.785 0.77625 0.6955 1 0.585 1C0.4745 1 0.385 0.77625 0.385 0.5C0.385 0.22375 0.4745 0 0.585 0C0.6955 0 0.785 0.22375 0.785 0.5Z" />
          <path d="M0.585 0.5C0.585 0.77625 0.454 1 0.2925 1C0.131 1 0 0.77625 0 0.5C0 0.22375 0.131 0 0.2925 0C0.454 0 0.585 0.22375 0.585 0.5Z" />
        </>
      );
    case "circle":
      return <circle cx="0.5" cy="0.5" r="0.5" />;
    default:
      return <circle cx="0.5" cy="0.5" r="0.5" />;
  }
};

const EventCard = ({
  variant = "default",
  eventType,
  date,
  endDate,
  title,
  image,
  maskShape = "four_vertical_even",
  ctaLabel,
  ctaLink,
  memberOnly,
  isMember,
  signedUp,
  dateChanged,
  isCancelled,
  action,
  onPreviewClick,
  onSignupClick,
  className,
  ...rest
}: EventCardProps) => {
  /**
   * Immer "Jetzt anmelden" — die Farbe des Knopfes zeigt den Zugang, nicht
   * der Text. Frueher stand hier "Kostenlos anmelden" oder "Jetzt Member
   * werden", je nach Zustand.
   *
   * Reisen bleibt die Ausnahme: Der Typ fuehrt zur Event-Seite statt zur
   * Anmeldung, weil eine Reise nicht mit einem Klick gebucht wird.
   */
  const resolvedCtaLabel = ctaLabel
    || (eventType === "reisen" ? "Mehr erfahren" : "Jetzt anmelden");

  const formattedDate = endDate
    ? formatEventDateRange(date, endDate)
    : format(date, "EEEE, dd. MMM yyyy", { locale: de });
  const isDefault = variant === "default";
  const isDensed = variant === "densed";

  const signedUpBadge = signedUp ? (
    <div className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-1",
      isCancelled
        ? "bg-red-100 text-white"
        : dateChanged
          // Auf einer Member-Only-Karte ist der Hintergrund selbst sun-50 —
          // das Abzeichen waere darin unsichtbar. Dann eine Stufe kraeftiger.
          ? memberOnly ? "bg-sun-100" : "bg-sun-50"
          : "bg-sky-50"
    )}>
      {isCancelled || dateChanged ? (
        <WarningTriangle className={cn("size-4 shrink-0", isCancelled ? "text-white" : "text-black")} />
      ) : (
        <CheckCircleFilled className="size-4 text-black shrink-0" />
      )}
      {/* `text-tag` wie beim Event-Badge daneben. Mit `text-subline-sm` war
          dieses Abzeichen ab dem md-Breakpoint groesser als der Badge, obwohl
          beide auf derselben Zeile stehen. */}
      <span className={cn("text-tag", isCancelled ? "text-white" : "text-black")}>
        {isCancelled ? "Termin abgesagt" : dateChanged ? "Termin geändert" : "Ich nehme teil"}
      </span>
    </div>
  ) : null;

  if (isDensed) {
    return (
      <article
        className={cn(
          "rounded-2xl p-3 w-full max-w-[360px] relative card-rotate",
          // Nur die kompakte Karte faerbt den Hintergrund: Sie hat keinen
          // Knopf, der den Zugang zeigen koennte, und traegt ihn deshalb in
          // der Flaeche. Die grosse Karte bleibt weiss — dort uebernimmt das
          // der Knopf.
          memberOnly ? "bg-sun-50" : "bg-white",
          className
        )}
        {...rest}
      >
        {signedUpBadge && (
          <div className="absolute right-3 top-3 z-10">{signedUpBadge}</div>
        )}
        {onPreviewClick ? (
          <div className="flex gap-4 cursor-pointer pr-8" onClick={onPreviewClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onPreviewClick()}>
            <div className="flex flex-col justify-between items-start">
              <div className="flex items-center gap-1 mb-4">
                {eventType && <EventTag variant={eventType} memberOnly={memberOnly} />}
                {memberOnly && <MemberLockIcon locked={!isMember} />}
              </div>
              <div className="flex flex-col">
                <p className="text-tag text-gray-100 truncate">{formattedDate}</p>
                <p className="text-headline-md-static text-black">{title}</p>
              </div>
            </div>
          </div>
        ) : (
          <Link href={ctaLink || "#"} className="flex gap-4 cursor-pointer pr-8">
            <div className="flex flex-col justify-between items-start">
              <div className="flex items-center gap-1 mb-4">
                {eventType && <EventTag variant={eventType} memberOnly={memberOnly} />}
                {memberOnly && <MemberLockIcon locked={!isMember} />}
              </div>
              <div className="flex flex-col">
                <p className="text-tag text-gray-100 truncate">{formattedDate}</p>
                <p className="text-headline-md-static text-black">{title}</p>
              </div>
            </div>
          </Link>
        )}
        {!onPreviewClick && <ArrowRightThin className="absolute right-3 top-1/2 -translate-y-1/2 shrink-0" />}
        {action && <div className="mt-3">{action}</div>}
      </article>
    );
  }

  const cardContent = (
    <>
      {/* Masked image */}
      <MaskedImage image={image} shape={maskShape} eventType={eventType} />

      {/* Content */}
      <div className="flex flex-col gap-3 items-center w-full">
        <div className="flex items-center gap-1">
          {eventType ? (
            <EventTag variant={eventType} memberOnly={memberOnly} />
          ) : (
            <span className="text-tag py-1 invisible">&nbsp;</span>
          )}
          {memberOnly && <MemberLockIcon locked={!isMember} />}
        </div>

        <div className="flex flex-col w-full text-center">
          <p className="text-tag text-gray-100 truncate">{formattedDate}</p>
          <p className="text-headline-md-static text-black">{title}</p>
        </div>
      </div>

    </>
  );

  /**
   * Die beiden Knoepfe am Fuss der grossen Karte.
   *
   * Frueher stand hier einer, und er lag zusammen mit Bild und Titel in einem
   * <Link>, der die ganze Karte umschloss — klickbar war also alles, mit
   * demselben Ziel. Zwei Ziele verlangen zwei Knoepfe ausserhalb dieses Links.
   *
   * Primaer: "Jetzt anmelden", schwarz bei kostenlosen Events, gelb bei
   * Member-Only. Fehlt der Zugang, steht ein Schloss neben dem Text.
   *
   * Sekundaer: "Zum Event" als Textlink mit Pfeil, ohne Flaeche — er soll den
   * primaeren nicht verdraengen.
   */
  const cardActions = signedUpBadge ? (
    <div className="flex justify-center mt-auto">{signedUpBadge}</div>
  ) : ctaLink && !onPreviewClick ? (
    <div className="flex flex-col items-center gap-3 mt-auto w-full">
      <Button
        variant={memberOnly ? "sun" : "black"}
        className="w-full"
        onClick={onSignupClick}
        asChild={!onSignupClick}
      >
        {/* `LockedIcon` statt `MemberLockIcon`: Letzteres bringt einen
            gelben Kreis mit, der auf dem gelben Knopf verschwaende. Im Knopf
            steht das Schloss nackt neben dem Text. */}
        {onSignupClick ? (
          <>
            {resolvedCtaLabel}
            {memberOnly && !isMember && <LockedIcon className="size-4 text-black shrink-0" />}
          </>
        ) : (
          <Link href={ctaLink}>
            {resolvedCtaLabel}
            {memberOnly && !isMember && <LockedIcon className="size-4 text-black shrink-0" />}
          </Link>
        )}
      </Button>
      <Link
        href={ctaLink}
        className="inline-flex items-center gap-2 text-copy text-black hover:opacity-70 transition-opacity"
      >
        Zum Event
        <ArrowRightThin className="size-4" />
      </Link>
    </div>
  ) : null;

  if (onPreviewClick) {
    return (
      <article
        className={cn(
          "bg-white rounded-2xl p-4 w-[278px] lg:w-[320px] flex flex-col gap-4 card-rotate cursor-pointer",
          className
        )}
        onClick={onPreviewClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onPreviewClick()}
        {...rest}
      >
        {cardContent}
        {/* Auf Werbeseiten ein einziger Knopf. Eine Anmeldung waere dort
            verfrueht, ein Link zur Event-Seite fuehrte aus dem Trichter —
            beides oeffnet stattdessen die Vorschau. */}
        <Button variant="black" className="w-full mt-auto">
          Mehr erfahren
        </Button>
      </article>
    );
  }

  if (ctaLink) {
    return (
      <article
        className={cn(
          "bg-white rounded-2xl p-4 w-[278px] lg:w-[320px] flex flex-col gap-4 card-rotate",
          className
        )}
        {...rest}
      >
        {/* Der Link umschliesst nur den Inhalt. Die Knoepfe stehen daneben,
            weil sie verschiedene Ziele haben. */}
        <div className="flex flex-col gap-4 flex-1">{cardContent}</div>
        {cardActions}
        {action && <div>{action}</div>}
      </article>
    );
  }

  return (
    <article
      className={cn(
        "bg-white rounded-2xl p-4 w-[278px] lg:w-[320px] flex flex-col gap-4 card-rotate",
        className
      )}
      {...rest}
    >
      {cardContent}
    </article>
  );
};

export { EventCard };
export type { EventCardProps, EventCardVariant };
